import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/context/I18nContext";
import { AuthProvider } from "@/context/AuthContext";
import { PortalProvider, usePortal } from "@/context/PortalContext";
import { PortalHeader } from "@/components/portal/PortalHeader";
import { Loader } from "@/components/portal/ui";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import AppEntry from "@/pages/AppEntry";
import PortalSelect from "@/pages/PortalSelect";
import AuthCallback from "@/pages/AuthCallback";
import ControlApp from "@/pages/control/ControlApp";
import AuthPage from "@/pages/portal/AuthPage";
import Dashboard from "@/pages/portal/Dashboard";
import Onboarding from "@/pages/portal/Onboarding";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <Loader />
    </div>
  );
}

function RootEntry() {
  const location = useLocation();
  if (location.hash.includes("session_id=")) {
    return <AuthCallback portalKey={null} redirectTo="/select" />;
  }
  return <AppEntry />;
}

function PortalInner() {
  const { portalKey, loading, error, reload } = usePortal();
  const location = useLocation();

  if (location.hash.includes("session_id=")) {
    return <AuthCallback portalKey={portalKey} redirectTo={`/p/${portalKey}/dashboard`} />;
  }
  if (loading) return <FullLoader />;
  if (error) {
    const notFound = error?.response?.status === 404;
    if (notFound) return <Navigate to="/" replace />;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: "var(--bg)" }} data-testid="portal-error">
        <p style={{ color: "var(--text-muted)" }}>Verbindung unterbrochen.</p>
        <button onClick={reload} className="px-5 h-11 font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: "var(--radius)" }} data-testid="portal-retry">
          Erneut versuchen
        </button>
      </div>
    );
  }

  const chromeless = location.pathname.endsWith("/auth") || location.pathname.endsWith("/onboarding");
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", color: "var(--text)" }}>
      {!chromeless && <PortalHeader />}
      <Routes>
        <Route index element={<Navigate to={`/p/${portalKey}/dashboard`} replace />} />
        <Route path="auth" element={<AuthPage />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="*" element={<Navigate to={`/p/${portalKey}/dashboard`} replace />} />
      </Routes>
    </div>
  );
}

function PortalLayout() {
  return (
    <PortalProvider>
      <PortalInner />
    </PortalProvider>
  );
}

function ControlLayout() {
  const location = useLocation();
  if (location.hash.includes("session_id=")) {
    return <AuthCallback portalKey={null} redirectTo="/control" />;
  }
  return <ControlApp />;
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<RootEntry />} />
              <Route path="/select" element={<PortalSelect />} />
              <Route path="/control/*" element={<ControlLayout />} />
              <Route path="/p/:portalKey/*" element={<PortalLayout />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
